import { useMemo } from 'react';
import { useFetchKnowledgebaseDb } from './useFetchKnowledgebaseDb';
import { routeRegistry } from '../navigation/routeRegistry';

const CATEGORY_DISPLAY_ORDER = [
  'Core',
  'Stats',
  'Predict',
  'Sales Drivers',
  'Marketing Drivers',
  'Workflows',
  'Setup',
];

/**
 * Hook that combines static route registry with live knowledgebase data.
 *
 * This enriches each route with:
 * - Actual tool data from knowledgebase (matched by UUID)
 * - Related tools data (full tool objects, not just IDs)
 * - Dynamic availability based on knowledgebase state
 *
 * The toolId in routeRegistry is a string like "gtm_stats" which matches
 * the knowledgebase tool UUID pattern. This hook resolves those IDs to
 * actual tool objects.
 *
 * Usage:
 * const { routes, isLoading, isError, getRouteById, findRouteByToolId } = useRouteRegistry();
 */
export const useRouteRegistry = () => {
  const { data: knowledgebase, isLoading, isError } = useFetchKnowledgebaseDb();

  /**
   * Enrich routes with knowledgebase tool data
   * Only runs when knowledgebase data changes
   */
  const enrichedRoutes = useMemo(() => {
    if (!knowledgebase?.db_all) {
      return routeRegistry.map(route => ({
        ...route,
        tool: null,
        relatedToolsData: [],
      }));
    }

    const tools = knowledgebase.db_all;

    return routeRegistry.map(route => {
      // Find primary tool for this route
      // Match by UUID (e.g., toolId "gtm_stats" should match tool.uuid "gtm_stats")
      const tool = route.toolId
        ? tools.find(t => t.uuid === route.toolId)
        : null;

      // Find related tools
      const relatedToolsData = (route.relatedTools || [])
        .map(toolId => tools.find(t => t.uuid === toolId))
        .filter(Boolean); // Remove null/undefined

      return {
        ...route,
        tool,
        relatedToolsData,
        // Add availability flag based on knowledgebase presence
        isToolAvailable: route.toolId ? Boolean(tool) : true,
      };
    });
  }, [knowledgebase?.db_all]);

  /**
   * Get route by ID
   */
  const getRouteById = useMemo(() => {
    return (routeId) => enrichedRoutes.find(route => route.id === routeId);
  }, [enrichedRoutes]);

  /**
   * Get routes by category
   */
  const getRoutesByCategory = useMemo(() => {
    return (category) => enrichedRoutes.filter(route => route.category === category);
  }, [enrichedRoutes]);

  /**
   * Get all unique categories
   */
  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(enrichedRoutes.map(route => route.category).filter(Boolean))];

    return uniqueCategories.sort((a, b) => {
      const aIndex = CATEGORY_DISPLAY_ORDER.indexOf(a);
      const bIndex = CATEGORY_DISPLAY_ORDER.indexOf(b);

      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;

      return aIndex - bIndex;
    });
  }, [enrichedRoutes]);

  /**
   * Find route by tool ID (from knowledgebase)
   * Returns the first route that uses this tool either as primary or related
   */
  const findRouteByToolId = useMemo(() => {
    return (toolId) => {
      return enrichedRoutes.find(route =>
        route.toolId === toolId || route.relatedTools?.includes(toolId)
      );
    };
  }, [enrichedRoutes]);

  /**
   * Search routes by query string
   * Searches across: label, description, tags, category, tool metadata
   */
  const searchRoutes = useMemo(() => {
    return (query) => {
      if (!query || query.trim() === '') return enrichedRoutes;

      const lowerQuery = query.toLowerCase();

      return enrichedRoutes.filter(route => {
        // Build searchable text from route metadata
        const searchableFields = [
          route.label,
          route.description,
          route.category,
          ...(route.tags || []),
          ...(route.searchAliases || []),
        ];

        if (route.tabs?.length > 0) {
          route.tabs.forEach(tab => {
            searchableFields.push(
              tab.id || '',
              tab.value || '',
              tab.label || '',
              ...(tab.keywords || []),
            );
          });
        }

        // Add tool metadata if available
        if (route.tool) {
          searchableFields.push(
            route.tool.name || '',
            route.tool.cmetadata?.summary || '',
            ...(route.tool.cmetadata?.analytic_intent || []),
            ...(route.tool.cmetadata?.domain || []),
            ...(route.tool.cmetadata?.entity_type || []),
          );
        }

        // Add related tools metadata
        if (route.relatedToolsData?.length > 0) {
          route.relatedToolsData.forEach(tool => {
            searchableFields.push(
              tool.name || '',
              tool.cmetadata?.summary || '',
              ...(tool.cmetadata?.analytic_intent || []),
              ...(tool.cmetadata?.domain || []),
              ...(tool.cmetadata?.entity_type || []),
            );
          });
        }

        const searchableText = searchableFields.join(' ').toLowerCase();

        return searchableText.includes(lowerQuery);
      });
    };
  }, [enrichedRoutes]);

  /**
   * Get hero routes (permanent, high-priority routes like Recipe Designer)
   */
  const heroRoutes = useMemo(() => {
    return enrichedRoutes.filter(route => route.isPermanent);
  }, [enrichedRoutes]);

  /**
   * Get routes organized by category
   */
  const routesByCategory = useMemo(() => {
    return categories.map(category => ({
      name: category,
      routes: enrichedRoutes.filter(r => r.category === category),
    })).filter(cat => cat.routes.length > 0);
  }, [enrichedRoutes, categories]);

  return useMemo(() => ({
    // Data
    routes: enrichedRoutes,
    categories,
    heroRoutes,
    routesByCategory,
    knowledgebase: knowledgebase?.db_all, // Export for suggestions without duplicate fetch

    // Loading states
    isLoading,
    isError,

    // Helper functions
    getRouteById,
    getRoutesByCategory,
    findRouteByToolId,
    searchRoutes,
  }), [enrichedRoutes, categories, heroRoutes, routesByCategory, knowledgebase, isLoading, isError, getRouteById, getRoutesByCategory, findRouteByToolId, searchRoutes]);
};

export default useRouteRegistry;
